import React, { useEffect, useState } from "react";

import tailwindBase from "~/css/base.css?url";
import legacySemantic from "~/css/semantic/legacy-semantic.css?url";
import semantic from "~/css/semantic.css?url";
import textVariables from "~/css/text-variables.css?url";
import textVariablesCompat from "~/css/text-variables-compat.css?url";
import legacyTextStyles from "~/ui/legacy-text-styles.css?url";

import StylesheetsContext from "./StylesheetsContext";
import type { UXSimplificationFlag } from "./UXFeatureFlags";

function useToggleStylesheet(url: string, enabled: boolean) {
	useEffect(() => {
		const link = document.querySelector<HTMLLinkElement>(`link[href="${url}"]`);

		if (!link) {
			console.warn(`Stylesheet link not found for url: ${url}`);
			return;
		}

		link.disabled = !enabled;
	}, [url, enabled]);
}

/**
 * This component manages the loading and unloading of CSS when UX Simplification is turned on and off.
 * It provides a context with a toggling method meant to be used when the user setting changes
 * It has no control over that setting.
 */
export function StylesheetsProvider({ children }: { children: React.ReactNode }) {
	const [uxSimplificationFlag, setUXSimplificationStylesEnabled] = useState<UXSimplificationFlag>(
		document.documentElement.dataset.uxSimplificationAppStructure === "enabled"
			? "WIP"
			: document.documentElement.dataset.uxSimplification === "enabled"
				? "EARLY_ACCESS"
				: "OFF",
	);

	const isUXSimplificationEnabled = uxSimplificationFlag !== "OFF";

	// New stylesheets
	useToggleStylesheet(semantic, isUXSimplificationEnabled);
	useToggleStylesheet(textVariables, isUXSimplificationEnabled);
	useToggleStylesheet(tailwindBase, isUXSimplificationEnabled);

	// Old stylesheets
	useToggleStylesheet(legacySemantic, !isUXSimplificationEnabled);
	// TODO: The legacy text styles will be necessary until we've completely removed all references to variables and fonts in it.
	useToggleStylesheet(legacyTextStyles, true);
	useToggleStylesheet(textVariablesCompat, !isUXSimplificationEnabled);

	// Update the data-ux-simplification attributes on the document element
	useEffect(() => {
		switch (uxSimplificationFlag) {
			case "WIP":
				document.documentElement.dataset.uxSimplification = "enabled";
				document.documentElement.dataset.uxSimplificationAppStructure = "enabled";
				break;
			case "EARLY_ACCESS":
				document.documentElement.dataset.uxSimplification = "enabled";
				document.documentElement.dataset.uxSimplificationAppStructure = "disabled";
				break;
			case "OFF":
				document.documentElement.dataset.uxSimplification = "disabled";
				document.documentElement.dataset.uxSimplificationAppStructure = "disabled";
				break;
		}
	}, [uxSimplificationFlag]);

	// Unrelated to the new UX — there are cases where the theme isn't set (i.e. the error page)
	// This is a fallback to system preference.
	useEffect(() => {
		if (
			document.documentElement.dataset.theme === "theme-dark" ||
			document.documentElement.dataset.theme === "theme-light"
		) {
			return;
		}

		const matchDarkMode = window.matchMedia("(prefers-color-scheme: dark)");
		const isDark = matchDarkMode.matches;
		if (isDark) {
			document.documentElement.dataset.theme = "theme-dark";
		} else {
			document.documentElement.dataset.theme = "theme-light";
		}
	}, []);

	return <StylesheetsContext.Provider value={setUXSimplificationStylesEnabled}>{children}</StylesheetsContext.Provider>;
}
